import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LogIn, ArrowRight, Eye, EyeOff } from 'lucide-react';
import GoogleJsonLogin from './GoogleJsonLogin';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    const [showVerification, setShowVerification] = useState(false);
    const [otp, setOtp] = useState('');

    useEffect(() => {
        // Check if redirected from signup
        const params = new URLSearchParams(location.search);
        if (params.get('registered') === 'true') {
            setSuccess('Account created successfully! Please log in.');
        }
        if (params.get('verified') === 'true') {
            setSuccess('Email verified successfully! Please log in.');
        }
    }, [location]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            if (response.status === 403) {
                const data = await response.json();
                if (data.error === 'unverified') {
                    setShowVerification(true);
                    setSuccess(data.message);
                    setLoading(false);
                    return;
                }
            }

            if (!response.ok) {
                throw new Error('Invalid credentials');
            }

            const data = await response.json();

            login(data.token, data.role, data.name);

            if (data.role === 'admin') {
                navigate('/admin/dashboard');
            } else {
                navigate('/student/dashboard');
            }

        } catch (err) {
            setError('Invalid email or password');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch('/api/verify-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otp })
            });

            if (!response.ok) {
                throw new Error('Invalid or expired OTP');
            }

            const data = await response.json();

            // Login success after verification
            login(data.token, data.role, data.name);

            navigate('/student/dashboard');

        } catch (err: any) {
            setError(err.message || 'Verification failed');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        try {
            await fetch('/api/resend-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            alert('OTP resent to your email');
        } catch (err) {
            console.error(err);
        }
    };

    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotStep, setForgotStep] = useState<'email' | 'otp'>('email');
    const [newPassword, setNewPassword] = useState('');

    const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (forgotStep === 'email') {
                await fetch('/api/forgot-password', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                });
                // Always say sent for security, or based on response
                setForgotStep('otp');
                setSuccess('If registered, an OTP has been sent to your email.');
            } else {
                // Reset Password
                const response = await fetch('/api/reset-password', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, otp, newPassword })
                });

                if (!response.ok) {
                    const data = await response.json();
                    throw new Error(data.message || 'Failed to reset password');
                }

                setSuccess('Password reset successfully! Please login with your new password.');
                setShowForgotPassword(false);
                setForgotStep('email');
                setOtp('');
                setNewPassword('');
                setPassword('');
            }
        } catch (err: any) {
            setError(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-[#009B8F]/10 text-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-4">
                        <LogIn size={32} />
                    </div>
                    <h2 className="text-2xl font-serif text-[#011E26]">
                        {showVerification ? 'Verify Email' : showForgotPassword ? 'Reset Password' : 'Welcome Back'}
                    </h2>
                    <p className="text-slate-500 text-sm mt-2">
                        {showVerification ? `Enter OTP sent to ${email}` : showForgotPassword ? (forgotStep === 'email' ? 'Enter your email to receive OTP' : 'Enter OTP and new password') : 'Sign in to your account'}
                    </p>
                </div>

                {success && (
                    <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-6 text-center border border-green-200">
                        {success}
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-6 text-center border border-red-200">
                        {error}
                    </div>
                )}

                {showVerification ? (
                    <form onSubmit={handleVerify} className="space-y-6">
                        <div>
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">One-Time Password</label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all text-center text-2xl tracking-widest"
                                placeholder="000000"
                                maxLength={6}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 bg-[#009B8F] hover:bg-[#00857a] text-white text-sm font-bold tracking-widest uppercase transition-all rounded-lg flex items-center justify-center gap-2 disabled:opacity-70"
                        >
                            {loading ? 'Verifying...' : 'Verify & Login'}
                        </button>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={handleResend}
                                className="text-sm text-[#009B8F] font-bold hover:underline"
                            >
                                Resend OTP
                            </button>
                        </div>
                        <div className="mt-8 text-center border-t border-gray-100 pt-6">
                            <button onClick={() => setShowVerification(false)} className="text-[#009B8F] font-bold hover:underline text-sm">
                                Back to Login
                            </button>
                        </div>
                    </form>
                ) : showForgotPassword ? (
                    <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
                        {forgotStep === 'email' ? (
                            <div>
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Email Address</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all"
                                    required
                                />
                            </div>
                        ) : (
                            <>
                                <div>
                                    <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">One-Time Password</label>
                                    <input
                                        type="text"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all text-center text-2xl tracking-widest"
                                        placeholder="000000"
                                        maxLength={6}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showNewPassword ? "text" : "password"}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all pr-12"
                                            required
                                            minLength={6}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#009B8F] focus:outline-none transition-colors"
                                        >
                                            {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 bg-[#009B8F] hover:bg-[#00857a] text-white text-sm font-bold tracking-widest uppercase transition-all rounded-lg flex items-center justify-center gap-2 disabled:opacity-70"
                        >
                            {loading ? 'Processing...' : (forgotStep === 'email' ? 'Send OTP' : 'Reset Password')}
                        </button>

                        <div className="mt-8 text-center border-t border-gray-100 pt-6">
                            <button type="button" onClick={() => { setShowForgotPassword(false); setSuccess(''); setError(''); }} className="text-[#009B8F] font-bold hover:underline text-sm">
                                Back to Login
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                            <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Email Address</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all"
                                required
                            />
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block">Password</label>
                                <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-[#009B8F] hover:underline font-bold">
                                    Forgot Password?
                                </button>
                            </div>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all pr-12"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#009B8F] focus:outline-none transition-colors"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 bg-[#009B8F] hover:bg-[#00857a] text-white text-sm font-bold tracking-widest uppercase transition-all rounded-lg flex items-center justify-center gap-2 disabled:opacity-70"
                        >
                            {loading ? 'Signing In...' : (
                                <>Sign In <ArrowRight size={16} /></>
                            )}
                        </button>
                    </form>
                )}

                {!showForgotPassword && !showVerification && (
                    <>
                        <div className="mt-8 text-center relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-100"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">Or continue with</span>
                            </div>
                        </div>

                        <div className="mt-6">
                            <GoogleJsonLogin />
                        </div>

                        <div className="mt-8 text-center">
                            <p className="text-slate-500 text-sm">
                                Don't have an account?{' '}
                                <Link to="/signup" className="text-[#009B8F] font-bold hover:underline">
                                    Sign up now
                                </Link>
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Login;
